// The inbox (spec 06 §Inbox; Work OS inbox-list.tsx): one inbox per person per company, newest
// first. INBOX-PREFS tabs: Waiting on me · Mentions · DMs · All; below them Unread · Approvals ·
// Followed · Memory · Snoozed · Done (Memory with the `memory` add-on, linking the memory review:
// UX pass 1.4). A waiting row says who waits and since when, with its one-click action
// (Approve inline, Reply, or Open). A row
// is an unread dot, the kind's icon, the summary and when; Enter or a click opens its subject
// (a task opens in Work's drawer) and marks it read; `e` marks it done (it leaves the inbox; the
// toast offers Undo); `s` snoozes it until tomorrow morning (M2-04; it comes back then, Undo
// wakes it now); `j` / `k` move. Snoozed and Done list what left, to wake or bring back. "Mark all read" clears the filter's unread items. Digital
// employees have no inbox (their equivalent is the wake-up queue), nor do agency users without a
// membership: the list says so rather than looking empty by mistake. AGT-UI-2: an approval the
// person decides shows its held post (or summary) and Approve / Edit / Reject in the row; a run
// waiting for review (DC-07's `agent_escalation`) has Resume; a disabled webhook opens Setup ›
// Webhooks.
import {
  Badge,
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  EmptyState,
  InfoTip,
  Segmented,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  AlarmClockIcon,
  ArrowLeftIcon,
  AtSignIcon,
  BellIcon,
  BellRingIcon,
  BotIcon,
  BrainIcon,
  CheckIcon,
  CircleAlertIcon,
  GaugeIcon,
  HandIcon,
  InboxIcon,
  ListFilterIcon,
  MailOpenIcon,
  MessageSquareIcon,
  ReceiptIcon,
  RefreshCwIcon,
  ReplyIcon,
  RotateCcwIcon,
  SirenIcon,
  UserPlusIcon,
  WalletIcon,
  WaypointsIcon,
  XIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { ApprovalRow } from "../../api/types";
import { approvalQuery, myApprovalsQuery } from "../agents/agent-queries";
import {
  ApprovalActions,
  DecisionOutcome,
  decisionToast,
  heldPostText,
} from "../agents/approval-actions";
import { AccessRequestActions } from "../agents/ask-access";
import { ContinueRunButton, FAILURE_STOP_LINE } from "../agents/continue-run";
import { EscalationCard, hasCard } from "../agents/escalation-card";
import { mayOfferResume, ResumeRunButton } from "../agents/resume-run";
import { remindersQuery } from "../chat/chat-switch";
import { ScreenAskInboxActions } from "../chat/screen-ask-card";
import type { CompanyAccess } from "../company";
import { digitalEmployeesQuery, peopleQuery } from "../queries";
import { useFeature, useSession } from "../use-feature";
import { focusRow, useListKeys } from "./list-keys";
import { RecoveryActions } from "./recovery-actions";
import { StructureChangeDialog } from "./structure-change-dialog";
import { TakeBackButton } from "./take-back";
import { ReviewStrip } from "./task-page/review-strip";
import { type InboxFilter, inboxQuery, invalidateWork } from "./work-queries";

type Item = Awaited<ReturnType<Api["client"]["inbox"]["list"]>>["data"][number];
/** INBOX-READABLE: the memory scope a Recall notice is about (with its name). */
type MemoryScopeRef = NonNullable<Item["memory_scope"]>;

// INBOX-PREFS: the main tabs; the earlier filters stay one row below.
const TABS: { value: InboxFilter; label: string }[] = [
  { value: "waiting", label: "Waiting on me" },
  { value: "mentions", label: "Mentions" },
  { value: "dms", label: "DMs" },
  { value: "all", label: "All" },
];

const FILTERS: { value: InboxFilter; label: string }[] = [
  { value: "unread", label: "Unread" },
  { value: "approvals", label: "Approvals" },
  { value: "followed", label: "Followed" },
  // UX pass 1.4: Recall's proposals (with the `memory` add-on; the memory review's queue).
  { value: "memory", label: "Memory" },
  { value: "snoozed", label: "Snoozed" },
  { value: "done", label: "Done" },
];

/**
 * AGT-UI-2: inbox items marked done because their approval settled, for the page's life (not one
 * mount: the inbox drawer remounts), so Undo on the "Marked done" toast holds.
 */
const AUTO_DONE = new Set<string>();

/** An agent's needs-you card, as its mention item's summary reads ("… needs you: log in …"). */
const NEEDS_YOU = / needs you: /;

/**
 * INBOX-READABLE: a row's writes (one per item of a memory scope's row) all run to the end
 * before the first failure rejects, so the list refreshes once, on the final state.
 */
async function settleAll<T>(writes: Promise<T>[]): Promise<T[]> {
  const out = await Promise.allSettled(writes);
  const failed = out.find((r): r is PromiseRejectedResult => r.status === "rejected");
  if (failed) throw failed.reason;
  return out.map((r) => (r as PromiseFulfilledResult<T>).value);
}

/** Tomorrow at 9:00 local time: when a snoozed item comes back. */
export function tomorrowMorning(now = new Date()): string {
  const t = new Date(now);
  t.setDate(t.getDate() + 1);
  t.setHours(9, 0, 0, 0);
  return t.toISOString();
}

const KIND: Record<Item["kind"], { label: string; icon: typeof InboxIcon }> = {
  mention: { label: "Mention", icon: AtSignIcon },
  reply: { label: "Reply", icon: MessageSquareIcon },
  task_change: { label: "Task change", icon: RefreshCwIcon },
  assignment: { label: "Assigned", icon: UserPlusIcon },
  approval: { label: "Approval", icon: HandIcon },
  reminder: { label: "Reminder", icon: BellIcon },
  budget_alert: { label: "Budget", icon: WalletIcon },
  agent_escalation: { label: "Needs you", icon: SirenIcon },
  system: { label: "System", icon: CircleAlertIcon },
  // M7-03: Recall's proposals and notices (the review screen is M7-04).
  memory: { label: "Memory", icon: BrainIcon },
  // SEARCH-NOTIF: a digital employee's run result; a seat's number due or late.
  agent_result: { label: "Agent result", icon: BotIcon },
  kpi_due: { label: "Numbers due", icon: GaugeIcon },
  // MEMBER-ENTRY: a seat holder corrected a past period's number.
  kpi_correction: { label: "Number corrected", icon: GaugeIcon },
  // COS-MODES: an agent directed one of the person's agents (Team · Tell me), with Take back.
  agent_directed: { label: "Agent directed", icon: WaypointsIcon },
};

/** A row's label and icon: its kind's, or its subject's where that says more. */
function kindOf(item: Item): { label: string; icon: typeof InboxIcon } {
  // SKILLSMITH-OPPORTUNITIES: a helper quote to approve or decline on Billing.
  if (item.subject_kind === "helper_offer") return { label: "Helper quote", icon: ReceiptIcon };
  return KIND[item.kind];
}

/** INBOX-READABLE: what each Filter view shows, behind the chip's (i). */
const FILTER_INFO: Record<InboxFilter, string> = {
  all: "Everything in your inbox.",
  waiting: "Approvals and questions a DE needs from you.",
  mentions: "Where someone @mentioned you.",
  dms: "Your direct messages.",
  unread: "Items you have not opened yet.",
  approvals: "Proposals from digital employees that need your decision.",
  followed: "Changes on tasks and topics you follow.",
  memory: "What Recall proposes to keep, one row per scope. The Memory tab decides them.",
  snoozed: "Snoozed items come back at 9:00 the next morning.",
  done: "Items you marked done. Bring one back with Not done.",
};

const EMPTY: Record<InboxFilter, [string, string]> = {
  all: [
    "You're all caught up.",
    "Mentions, task changes on what you follow, reminders and agents that need you land here.",
  ],
  waiting: ["Nothing is waiting on you.", "Approvals and questions a DE needs from you land here."],
  dms: ["No new direct messages.", "Messages in your DMs land here."],
  unread: ["Nothing unread.", "New mentions, assignments and replies land here."],
  mentions: ["No mentions.", "When someone @mentions you in a thread it shows here."],
  approvals: [
    "No approvals waiting.",
    "Proposals from digital employees that need your decision land here.",
  ],
  followed: [
    "Nothing new on what you follow.",
    "Changes to tasks and topics you follow land here, one row per task per hour.",
  ],
  memory: ["No memory proposals.", "What Recall proposes to keep or drop lands here."],
  snoozed: ["Nothing snoozed.", "Press s on an item to bring it back tomorrow morning."],
  done: ["Nothing done yet.", "Items you mark done land here, to bring back if needed."],
};

/** "now", "12 min", "3 h", then the day. */
export function when(iso: string, now = new Date()): string {
  const mins = Math.round((now.getTime() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins} min`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)} h`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function InboxList({
  api,
  company,
  onNavigated,
  split = false,
  peek = false,
  limit,
  keysOnWindow = false,
}: {
  api: Api;
  company: CompanyAccess;
  /** Called after a row opened its subject (the peek closes). */
  onNavigated?: () => void;
  /**
   * INBOX-PAGE-MAIN: the top bar's quick peek — Waiting on me only, no tabs, Filter or key hints;
   * rows keep their inline primary action (Approve / Reply / Resume / Open).
   */
  peek?: boolean;
  /** At most this many rows (the peek's top 5). */
  limit?: number;
  /**
   * INBOX-PAGE-MAIN: j/k/e/s/u/⏎ answer anywhere on the page (not only once focus is in the
   * list), so the Inbox page is keyboard-first from the moment it opens.
   */
  keysOnWindow?: boolean;
  /**
   * INBOX-READABLE: the Inbox page's layout — the list on the left, the picked item on the right
   * with its actions (decide inline, reply, open its source). A row then selects instead of
   * leaving the page; "Open" in the pane goes to the source.
   */
  split?: boolean;
}) {
  // INBOX-READABLE: the split page's picked row.
  const [opened, setOpened] = useState<Entry | null>(null);
  const selectedKey = opened?.key ?? null;
  const memoryOn = useFeature("memory", company.id).enabled;
  const [chosen, setFilter] = useState<InboxFilter | null>(null);
  /** STRUCTURE-CHANGED: the structure change whose modal is open. */
  const [change, setChange] = useState<string | null>(null);
  // INBOX-READABLE: removing the Filter chip goes back to the tab it was picked from.
  const [lastTab, setLastTab] = useState<InboxFilter>("all");
  // INBOX-PREFS: until the person picks a tab, the inbox opens on Waiting on me when something
  // waits, otherwise on All (the waiting list is the same query the tab shows: no extra load).
  const waiting = useQuery(inboxQuery(api, company.id, "waiting"));
  const opening: InboxFilter =
    waiting.isPending || (waiting.data?.data.length ?? 0) > 0 ? "waiting" : "all";
  const picked: InboxFilter = peek ? "waiting" : (chosen ?? opening);
  // Once known, the opening tab stays put (answering the last waiting item does not jump to All).
  useEffect(() => {
    if (chosen === null && !waiting.isPending) {
      setFilter(opening);
      setLastTab(opening);
    }
  }, [chosen, waiting.isPending, opening]);
  // The Memory filter only with the `memory` add-on (it falls back to All when that goes off).
  const filter: InboxFilter = picked === "memory" && !memoryOn ? "all" : picked;
  const filters = memoryOn ? FILTERS : FILTERS.filter((f) => f.value !== "memory");
  const list = useQuery(inboxQuery(api, company.id, filter));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const root = useRef<HTMLDivElement>(null);
  const co = company.id;
  const { me: session } = useSession();
  const meId = session.memberships.find((m) => m.company_id === co)?.membership_id ?? null;
  const refresh = () => invalidateWork(queryClient, co);

  // INBOX-READABLE: each takes a row's items (a memory row stands for every notice of its scope).
  const read = useMutation({
    mutationFn: (v: { ids: readonly string[]; read: boolean }) =>
      settleAll(
        v.ids.map((id) => api.client.inbox.read({ params: { co, id }, body: { read: v.read } })),
      ),
    onSettled: refresh,
  });
  const done = useMutation({
    // `quiet`: the row's own action already said what happened (Take back's toast).
    mutationFn: (v: { ids: readonly string[]; done: boolean; quiet?: boolean }) =>
      settleAll(
        v.ids.map((id) => api.client.inbox.done({ params: { co, id }, body: { done: v.done } })),
      ),
    onSuccess: (_r, v) => {
      if (v.done && !v.quiet)
        toast.add({
          title: "Marked done",
          description: "It left the inbox.",
          actionProps: {
            children: "Undo",
            onClick: () => done.mutate({ ids: v.ids, done: false }),
          },
        });
    },
    // Mutation-level (runs even if this list unmounted or a later mutate superseded it): a
    // failed completion clears the auto-done guard so the next settle retries.
    onError: (_e, v) => {
      if (v.done) for (const id of v.ids) AUTO_DONE.delete(id);
    },
    onSettled: refresh,
  });
  const snooze = useMutation({
    mutationFn: (v: { ids: readonly string[]; until: string | null }) =>
      settleAll(
        v.ids.map((id) =>
          api.client.inbox.snooze({ params: { co, id }, body: { until: v.until } }),
        ),
      ),
    onSuccess: (_r, v) => {
      if (v.until)
        toast.add({
          title: "Snoozed until tomorrow",
          description: "It comes back at 9:00.",
          actionProps: {
            children: "Undo",
            onClick: () => snooze.mutate({ ids: v.ids, until: null }),
          },
        });
    },
    onSettled: refresh,
  });
  const readAll = useMutation({
    mutationFn: () =>
      api.client.inbox.readAll({
        params: { co },
        body: filter === "all" || filter === "done" || filter === "snoozed" ? {} : { filter },
      }),
    onSuccess: (r) =>
      toast.add({ title: r.count === 1 ? "1 item marked read" : `${r.count} items marked read` }),
    onSettled: refresh,
  });

  const items = list.data?.data ?? [];
  const unread = items.filter((i) => !i.read_at).length;
  // INBOX-PREFS: who a waiting item is from (DEs from the cached roster, people from the
  // directory, read only when a waiting row names someone).
  const names = useMemberNames(
    api,
    co,
    items.some((i) => isWaiting(i) && i.from_membership_id),
  );
  // AGT-UI-2: an approval row decides inline. The caller's pending decisions (`GET my/approvals`,
  // one call, shared with the roster) give the rows their approval and say who decides: a row
  // without a pending approval of the caller's only opens it.
  const agentsOn = useFeature("agents", co).enabled;
  const live = (i: Item) => !i.done_at;
  const mine = useQuery({
    ...myApprovalsQuery(api, co),
    enabled: agentsOn && items.some((i) => i.subject_kind === "approval" && live(i)),
  });
  const pending = useMemo(
    () => new Map((mine.data?.data ?? []).map((a) => [a.id, a] as const)),
    [mine.data],
  );
  const reviewer = mayOfferResume(company);
  // Decided here this session: the row keeps the outcome after the approval leaves the pending
  // queue (the approval page and the Agents list carry it for good).
  // Scoped to its company, like the Approvals screen's: another company starts empty.
  const [decidedIn, setDecidedIn] = useState<{ co: string; map: ReadonlyMap<string, ApprovalRow> }>(
    { co, map: new Map() },
  );
  const decided = decidedIn.co === co ? decidedIn.map : new Map<string, ApprovalRow>();
  const setDecided = (
    f: (m: ReadonlyMap<string, ApprovalRow>) => ReadonlyMap<string, ApprovalRow>,
  ) => setDecidedIn((s) => ({ co, map: f(s.co === co ? s.map : new Map()) }));
  // Rows marked done once their decision settled: once only (AUTO_DONE), so Undo holds.
  const settle = (id: string) => {
    if (AUTO_DONE.has(id)) return;
    AUTO_DONE.add(id);
    // A failed request clears the guard (the mutation's own onError) so the next settle retries.
    done.mutate({ ids: [id], done: true });
  };
  const inline = (item: Item): ReactNode => {
    // REACH-S1: an access request to an agent this person serves: Grant / Decline in the row
    // (core: a personal agent's owner decides too), done once decided.
    if (item.subject_kind === "reach_request" && live(item))
      return (
        <span className="mt-1 flex">
          <AccessRequestActions
            api={api}
            co={co}
            requestId={item.subject_id}
            onDecided={() => settle(item.id)}
          />
        </span>
      );
    // COS-MODES: an agent directed the person's agent: Take back on the row, then it is done.
    if (item.kind === "agent_directed" && item.subject_kind === "task" && live(item))
      return (
        <TakeBackButton
          api={api}
          co={co}
          taskId={item.subject_id}
          eventId={item.source_event_id}
          summary={item.summary}
          onTakenBack={() => done.mutate({ ids: [item.id], done: true, quiet: true })}
        />
      );
    // BOARD-GATES: a card waiting in a person-only column: Approve / Rework / Reject for whoever
    // decides it (the card strip's own logic; nothing for a column without a gate).
    if (item.subject_kind === "task" && item.review_gate && live(item))
      return (
        <ReviewStrip
          api={api}
          co={co}
          taskId={item.subject_id}
          status={item.review_gate.column}
          variant="row"
          gate={item.review_gate}
        />
      );
    if (!agentsOn || !live(item)) return null;
    // SCREEN-HANDOFF: an agent's needs-you card (its message id is the ask's id): Open screen,
    // Hand back, Start a new screen, right on the row.
    if (item.subject_kind === "message" && item.message_id && NEEDS_YOU.test(item.summary))
      return (
        <ScreenAskInboxActions
          api={api}
          co={co}
          slug={company.slug}
          askId={item.message_id}
          me={meId}
        />
      );
    const outcome = decided.get(item.id);
    if (outcome)
      return (
        <DecisionOutcome
          api={api}
          co={co}
          approval={outcome}
          // Applied later (the executor runs after the decision): the row is done then.
          onSettled={() => settle(item.id)}
        />
      );
    // The row now shows the live outcome; it is marked done once that settles.
    const onDecided = (d: ApprovalRow) => setDecided((m) => new Map(m).set(item.id, d));
    const approval = item.subject_kind === "approval" ? pending.get(item.subject_id) : undefined;
    if (approval?.state === "pending")
      return <InlineApproval api={api} co={co} approval={approval} onDecided={onDecided} />;
    // Not on the queue's first page, or already decided (here before the inbox was closed, or
    // elsewhere): read on its own — Approve / Edit / Reject while pending, else its live outcome,
    // and the row is done once that settles.
    if (item.subject_kind === "approval" && !approval && mine.isSuccess)
      return (
        <InlineApprovalById
          api={api}
          co={co}
          id={item.subject_id}
          onDecided={onDecided}
          onSettled={() => settle(item.id)}
        />
      );
    // lane B (REQUEST-RECOVERY): an interrupted request's one item: Continue / Take over /
    // Dismiss through its resolve route (the server rechecks who may at the click).
    if (item.kind === "agent_escalation" && item.subject_kind === "recovery")
      return (
        <RecoveryActions
          api={api}
          co={co}
          recoveryId={item.subject_id}
          onResolved={() => done.mutate({ ids: [item.id], done: true })}
        />
      );
    // FAILURE-STOP: a run stopped after repeated failures: Continue (clears the stop, or wakes the
    // DE on the task again once the run ended) or Dismiss (the item is done).
    if (
      reviewer &&
      item.kind === "agent_escalation" &&
      item.subject_kind === "run" &&
      FAILURE_STOP_LINE.test(item.summary)
    )
      return (
        <span className="mt-1 flex gap-2" data-slot="failure-stop-actions">
          <ContinueRunButton
            api={api}
            co={co}
            runId={item.subject_id}
            size="xs"
            onContinued={() => done.mutate({ ids: [item.id], done: true })}
          />
          <Button
            size="xs"
            variant="outline"
            onClick={() => done.mutate({ ids: [item.id], done: true })}
            data-slot="dismiss-failure-stop"
          >
            Dismiss
          </Button>
        </span>
      );
    // DC-07: a run parked for review (or a premise escalation) resumes from its inbox item.
    if (reviewer && item.kind === "agent_escalation" && item.subject_kind === "run")
      return (
        <span className="mt-1 flex">
          <ResumeRunButton
            api={api}
            co={co}
            runId={item.subject_id}
            size="xs"
            onResumed={() => done.mutate({ ids: [item.id], done: true })}
          />
        </span>
      );
    return null;
  };

  function open(entry: Entry) {
    const item = entry.lead;
    const unreadIds = entry.items.filter((i) => !i.read_at).map((i) => i.id);
    if (unreadIds.length) read.mutate({ ids: unreadIds, read: true });
    const slug = company.slug;
    if (item.subject_kind === "structure_change") {
      // STRUCTURE-CHANGED §3.2: a reporting-line move opens its modal over the inbox (re-route).
      setChange(item.subject_id);
      return;
    }
    if (item.subject_kind === "task") {
      // A task opens in Work's drawer, over the Work page already open when there is one.
      const inWork = pathname.startsWith(`/c/${slug}/work`);
      void navigate({
        to: (inWork ? pathname : "/c/$slug/work") as never,
        params: { slug } as never,
        search: ((prev: Record<string, unknown>) => ({
          ...(inWork ? prev : {}),
          task: item.subject_id,
        })) as never,
      });
    } else if (item.subject_kind === "page") {
      // UPLOAD-PRIVATE-FIRST: an uploaded doc's text was read (or not): the doc.
      void navigate({
        to: "/c/$slug/work/p/$pageId",
        params: { slug, pageId: item.subject_id },
      });
    } else if (item.subject_kind === "commitment") {
      // CLOSED-LOOPS: a commitment (asked of me, by me, escalated) opens in the Inbox's
      // Commitments card: its details, its row in view.
      void navigate({
        to: "/c/$slug/inbox",
        params: { slug },
        search: { commitment: item.subject_id } as never,
      });
    } else if (item.kind === "memory") {
      // RECALL-INBOX-LOOP: memory rows open the Inbox's Memory lane (its card for a decision's
      // check), where the proposals are decided; the old memory review is no longer their door.
      void navigate({
        to: "/c/$slug/inbox",
        params: { slug },
        search: {
          tab: "memory",
          ...(item.subject_kind === "decision" ? { card: `outcome:${item.subject_id}` } : {}),
        } as never,
      });
    } else if (item.subject_kind === "message" && item.message_id) {
      // The exact spot: the conversation (and reply thread) the message is in, on the message.
      void navigate({
        to: "/c/$slug/chat/m/$messageId",
        params: { slug, messageId: item.message_id },
      });
    } else if (item.subject_kind === "message" || item.subject_kind === "topic") {
      void navigate({ to: "/c/$slug/chat", params: { slug } });
    } else if (item.subject_kind === "reminder") {
      // CHAT-SWITCH-A: a person's own chat reminder opens Saved (which lists it, opening its
      // message while they can still read it); a seat's (crew, Recall) reminder goes Home.
      void queryClient
        .fetchQuery(remindersQuery({ api, co }))
        .then((r) => r.data.some((x) => x.id === item.subject_id))
        .catch(() => false)
        .then((mine) =>
          navigate(
            mine
              ? { to: "/c/$slug/chat/saved", params: { slug } }
              : { to: "/c/$slug/desk", params: { slug } },
          ),
        );
    } else if (item.subject_kind === "approval") {
      // M3-08: an approval opens its proposal; a run its step timeline.
      void navigate({
        to: "/c/$slug/agents/approvals/$id",
        params: { slug, id: item.subject_id },
      });
    } else if (item.subject_kind === "run") {
      void navigate({ to: "/c/$slug/agents/runs/$id", params: { slug, id: item.subject_id } });
    } else if (item.subject_kind === "delegation_request") {
      void navigate({
        to: "/c/$slug/agents/delegations/$id",
        params: { slug, id: item.subject_id },
      });
    } else if (item.subject_kind === "portfolio_company") {
      // FOUNDER-OS phase 1: a portfolio alert opens the held company's page in Founder OS.
      void navigate({
        to: "/c/$slug/portfolio/companies/$companyId",
        params: { slug, companyId: item.subject_id },
      });
    } else if (item.subject_kind === "observation") {
      void navigate({ to: "/c/$slug/kpis/scorecard", params: { slug } });
    } else if (item.subject_kind === "budget") {
      // M3-04 / DE-BUDGET-PACE: a budget alert (threshold, pace, reserve) opens that budget.
      void navigate({
        to: "/c/$slug/setup/budgets/$id",
        params: { slug, id: item.subject_id },
      });
    } else if (item.subject_kind === "webhook") {
      // AGT-UI-2: a webhook disabled after 24 h of failures opens Setup › Webhooks.
      void navigate({ to: "/c/$slug/setup/webhooks", params: { slug } });
    } else if (item.subject_kind === "inbound_webhook") {
      // WEBHOOKS-IN: an incoming hook that paused itself opens the Incoming tab.
      void navigate({
        to: "/c/$slug/setup/webhooks",
        params: { slug },
        search: { tab: "incoming" } as never,
      });
    } else if (item.subject_kind === "de_brief") {
      // COS-BRIEF-RULES: a Chief of Staff report delivered to the inbox opens its full page.
      void navigate({
        to: "/c/$slug/briefs/$id" as never,
        params: { slug, id: item.subject_id } as never,
      });
    } else if (item.subject_kind === "agent_request") {
      // MANAGED-POLICY: a request for an agent (or its outcome) opens the DE list's Requests.
      void navigate({ to: "/c/$slug/agents", params: { slug } });
    } else if (item.subject_kind === "model_access") {
      // MODEL-KEYS-PLANS: the plan stopped allowing own keys: Connections › API keys.
      void navigate({
        to: "/c/$slug/agents/connections" as never,
        params: { slug } as never,
        search: { tab: "keys" } as never,
      });
    } else if (
      item.subject_kind === "wallet" ||
      item.subject_kind === "company_subscription" ||
      item.subject_kind === "helper_offer"
    ) {
      // WALLET-P1: credit low or used up opens Setup › Billing; BILLING-PLANS: so does a payment
      // that failed, its reminder, the suspension and the restore (pay there);
      // SKILLSMITH-OPPORTUNITIES: and a helper quote (Approve / Decline there).
      void navigate({ to: "/c/$slug/setup/billing" as never, params: { slug } as never });
    } else if (item.subject_kind === "seat" && item.kind === "budget_alert") {
      // DE-BUDGET-PACE: a DE's pace, reserve or new-period notice opens its seat drawer on Money,
      // where its budget panel is — a supervisor who is no admin may open it too.
      void navigate({
        to: "/c/$slug/kpis/chart",
        params: { slug },
        search: { seat: item.subject_id, lens: "finance" } as never,
      });
    } else if (item.subject_kind === "seat") {
      // SEARCH-NOTIF: a number due or late opens Enter numbers.
      void navigate({ to: "/c/$slug/kpis/entry/numbers" as never, params: { slug } as never });
    } else if (item.subject_kind === "action_outbox") {
      // STEP1-OUTBOX: a dead letter opens the Outbox's failed items (Retry or Cancel there).
      void navigate({
        to: "/c/$slug/agents/outbox" as never,
        params: { slug } as never,
        search: { tab: "failed", item: item.subject_id } as never,
      });
    } else if (item.subject_kind === "integration_job") {
      // INC2-INTAKE: an integration dead letter opens Intake's dead letters (Retry or Discard).
      void navigate({
        to: "/c/$slug/agents/intake" as never,
        params: { slug } as never,
        search: { tab: "dead" } as never,
      });
    } else if (item.subject_kind === "chief_of_staff") {
      // COS-MODES: the CEO's "switch to Team?" suggestion opens Setup › Chief of Staff.
      void navigate({ to: "/c/$slug/setup/chief-of-staff", params: { slug } });
    } else if (item.subject_kind === "reporting_duty") {
      // AGENT-REPORTING: a duty's reminder or late item opens Setup › KPI reporting.
      void navigate({ to: "/c/$slug/setup/kpi-reporting", params: { slug } });
    } else if (item.subject_kind === "billing_company") {
      // BILLING-PLANS: a client's failed billing change opens that company in Operator › Billing
      // (Retry / Dismiss there).
      void navigate({
        to: "/operator/billing",
        search: { tab: "subscriptions", company: item.subject_id },
      });
    } else {
      void navigate({ to: "/c/$slug/desk", params: { slug } });
    }
    onNavigated?.();
  }

  // INBOX-READABLE: Recall's notices fold into one row per scope.
  const entries = useMemo(
    () => (limit ? groupEntries(items).slice(0, limit) : groupEntries(items)),
    [items, limit],
  );
  const byKey = useMemo(() => new Map(entries.map((e) => [e.key, e])), [entries]);
  const ids = (e: Entry) => e.items.map((i) => i.id);
  // Done or snoozed from the page's pane: the item leaves, and so does the pane.
  const leave = (e: Entry) => {
    if (selectedKey === e.key) setOpened(null);
  };
  const markDone = (e: Entry) => {
    leave(e);
    done.mutate({ ids: ids(e), done: !e.lead.done_at });
  };
  const markSnoozed = (e: Entry) => {
    leave(e);
    snooze.mutate({ ids: ids(e), until: snoozedNow(e.lead) ? null : tomorrowMorning() });
  };
  const markRead = (e: Entry) => {
    const unreadNow = e.items.some((i) => !i.read_at);
    read.mutate({ ids: ids(e), read: unreadNow });
  };
  // The split page selects a row (and marks it read); the drawer opens its subject.
  const pick = (e: Entry) => {
    if (!split) return open(e);
    setOpened(e);
    const unreadIds = e.items.filter((i) => !i.read_at).map((i) => i.id);
    if (unreadIds.length) read.mutate({ ids: unreadIds, read: true });
  };
  // The live row while the list holds it; else the row as picked (Unread drops a row once it is
  // read, which opening it does: the pane stays on it until another is picked).
  const selected = selectedKey ? (byKey.get(selectedKey) ?? opened) : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the handlers read the latest rows through `byKey`.
  const actions = useMemo(
    () => ({
      onOpen: (row: HTMLElement) => {
        const entry = byKey.get(row.dataset.itemId ?? "");
        if (entry) pick(entry);
      },
      letters: {
        e: (row: HTMLElement) => {
          const entry = byKey.get(row.dataset.itemId ?? "");
          if (!entry) return;
          // Focus moves to the next row before this one leaves.
          focusRow(
            (row.nextElementSibling as HTMLElement | null) ??
              (row.previousElementSibling as HTMLElement | null),
          );
          markDone(entry);
        },
        s: (row: HTMLElement) => {
          const entry = byKey.get(row.dataset.itemId ?? "");
          if (!entry || entry.lead.done_at) return;
          focusRow(
            (row.nextElementSibling as HTMLElement | null) ??
              (row.previousElementSibling as HTMLElement | null),
          );
          markSnoozed(entry);
        },
        u: (row: HTMLElement) => {
          const entry = byKey.get(row.dataset.itemId ?? "");
          if (entry) markRead(entry);
        },
      },
    }),
    [byKey],
  );
  useListKeys(root, actions, true, keysOnWindow);
  const extra = FILTERS.find((f) => f.value === filter) ?? null;

  return (
    <div
      ref={root}
      data-slot="inbox"
      data-layout={split ? "split" : "list"}
      className={cn("flex min-h-0 min-w-0 flex-1", split ? "flex-row" : "flex-col")}
    >
      <div
        data-slot="inbox-list-pane"
        className={cn(
          "flex min-h-0 min-w-0 flex-col",
          split
            ? cn(
                "w-full md:w-[26rem] md:shrink-0 md:border-r md:border-subtle lg:w-[30rem]",
                selected && "max-md:hidden",
              )
            : "flex-1",
        )}
      >
        {/* INBOX-READABLE: one row — the tabs, then Filter (the other views) and Mark all read.
            INBOX-PAGE-MAIN: the peek has none (it is Waiting on me only; the page has them). */}
        {peek ? null : (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-subtle px-3 py-2">
            <Segmented
              size="sm"
              aria-label="Inbox tabs"
              options={TABS}
              value={filter}
              onValueChange={(v) => {
                setLastTab(v);
                setFilter(v);
              }}
            />
            <div className="ml-auto flex shrink-0 items-center gap-0.5">
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={extra ? `Filter: ${extra.label}` : "Filter"}
                      className={cn(extra && "text-primary-text")}
                    />
                  }
                >
                  <ListFilterIcon data-icon="inline-start" />
                  Filter
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-44">
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>Show only</DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                      value={extra ? filter : ""}
                      onValueChange={(v) => setFilter(v as InboxFilter)}
                    >
                      {filters.map((f) => (
                        <DropdownMenuRadioItem key={f.value} value={f.value}>
                          {f.label}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Mark all read"
                      disabled={!unread || readAll.isPending}
                      onClick={() => readAll.mutate()}
                    />
                  }
                >
                  <MailOpenIcon aria-hidden="true" />
                </TooltipTrigger>
                <TooltipContent>Mark all read</TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}
        {extra ? (
          <div
            data-slot="inbox-filter-chip"
            className="flex min-h-10 items-center gap-1.5 border-b border-subtle px-4 py-1.5"
          >
            <span className="inline-flex h-6 items-center gap-0.5 rounded-full border border-border bg-secondary pr-0.5 pl-2.5 text-xs font-medium text-foreground">
              {extra.label}
              <button
                type="button"
                aria-label={`Remove filter: ${extra.label}`}
                onClick={() => setFilter(lastTab)}
                className="flex size-5 items-center justify-center rounded-full text-muted-foreground outline-none hover:bg-sunken hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
              >
                <XIcon aria-hidden="true" className="size-3" />
              </button>
            </span>
            <InfoTip label={`About ${extra.label}`}>{FILTER_INFO[extra.value]}</InfoTip>
            {filter === "memory" ? (
              <Link
                data-slot="inbox-memory-review"
                to="/c/$slug/inbox"
                params={{ slug: company.slug }}
                search={{ tab: "memory" } as never}
                onClick={() => onNavigated?.()}
                className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-primary-text hover:underline"
              >
                <BrainIcon aria-hidden="true" className="size-3.5" />
                Open Memory
              </Link>
            ) : null}
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {list.isPending ? (
            <div role="status" aria-label="Loading the inbox" className="flex flex-col gap-2 p-4">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
          ) : list.data === null ? (
            <EmptyState
              variant="compact"
              icon={CircleAlertIcon}
              title="The inbox is not live on this server yet"
              description="Its routes arrive with the inbox build (M2-04). Nothing here is sample data."
            />
          ) : list.isError ? (
            <EmptyState
              variant="compact"
              icon={CircleAlertIcon}
              title="The inbox could not be loaded"
              description="Try again in a moment."
            />
          ) : !items.length ? (
            <EmptyState
              variant="compact"
              icon={BellRingIcon}
              title={EMPTY[filter][0]}
              description={EMPTY[filter][1]}
            />
          ) : (
            <ul aria-label="Inbox items" className="m-0 list-none p-0">
              {entries.map((entry) => (
                <InboxRow
                  key={entry.key}
                  entry={entry}
                  // The split page decides in its right-hand pane; the drawer in the row.
                  inline={!split && entry.items.length === 1 ? inline(entry.lead) : null}
                  from={
                    entry.lead.from_membership_id
                      ? names.get(entry.lead.from_membership_id)
                      : undefined
                  }
                  selected={split && selectedKey === entry.key}
                  onOpen={() => pick(entry)}
                  onReview={() => open(entry)}
                  onRead={() => markRead(entry)}
                  onDone={() => markDone(entry)}
                  onSnooze={() => markSnoozed(entry)}
                />
              ))}
            </ul>
          )}
        </div>
        <p
          className={cn(
            "flex h-7 shrink-0 items-center gap-1 border-t border-subtle px-4 text-2xs text-muted-foreground",
            peek && "hidden",
          )}
        >
          <kbd className="font-sans">j</kbd>/<kbd className="font-sans">k</kbd> move ·{" "}
          <kbd className="font-sans">⏎</kbd> open · <kbd className="font-sans">e</kbd> done ·{" "}
          <kbd className="font-sans">s</kbd> snooze · <kbd className="font-sans">u</kbd> read
        </p>
      </div>
      {split ? (
        <section
          data-slot="inbox-detail"
          aria-label="Inbox item"
          className={cn("min-h-0 min-w-0 flex-1 overflow-y-auto", !selected && "max-md:hidden")}
        >
          {selected ? (
            <InboxDetail
              entry={selected}
              inline={selected.items.length === 1 ? inline(selected.lead) : null}
              from={
                selected.lead.from_membership_id
                  ? names.get(selected.lead.from_membership_id)
                  : undefined
              }
              onBack={() => setOpened(null)}
              onOpen={() => open(selected)}
              onRead={() => markRead(selected)}
              onDone={() => markDone(selected)}
              onSnooze={() => markSnoozed(selected)}
            />
          ) : (
            <EmptyState
              variant="compact"
              icon={InboxIcon}
              title="Pick an item"
              description="It opens here, with its actions."
            />
          )}
        </section>
      ) : null}
      {change ? (
        <StructureChangeDialog
          api={api}
          co={company.id}
          id={change}
          onClose={() => {
            setChange(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

/** Where "Open" takes an item, in a word or two. */
function openLabel(item: Item): string {
  if (item.kind === "memory") return "Open Memory";
  if (item.subject_kind === "task") return "Open task";
  if (item.subject_kind === "page") return "Open doc";
  if (item.subject_kind === "message" || item.subject_kind === "topic") return "Open in chat";
  if (item.subject_kind === "approval") return "Open approval";
  if (item.subject_kind === "run") return "Open run";
  if (item.subject_kind === "commitment") return "Open commitment";
  return "Open";
}

/**
 * INBOX-READABLE: the split page's right-hand pane — the item in full, its inline decision
 * (Approve / Edit / Reject, Resume, Grant), Reply or Open on its source, and Done / Snooze / Read.
 * A memory scope's row lists its notices and reviews them on the memory review.
 */
function InboxDetail({
  entry,
  inline,
  from,
  onBack,
  onOpen,
  onRead,
  onDone,
  onSnooze,
}: {
  entry: Entry;
  inline: ReactNode;
  from: string | undefined;
  onBack: () => void;
  onOpen: () => void;
  onRead: () => void;
  onDone: () => void;
  onSnooze: () => void;
}) {
  const item = entry.lead;
  const kind = kindOf(item);
  const Icon = kind.icon;
  const unread = entry.items.some((i) => !i.read_at);
  const snoozed = snoozedNow(item);
  const reply =
    item.subject_kind === "message" ||
    (item.kind === "agent_escalation" && item.subject_kind !== "commitment");
  return (
    <article className="flex flex-col gap-4 p-5" data-item-id={entry.key}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Back to the list"
          onClick={onBack}
          className="md:hidden"
        >
          <ArrowLeftIcon aria-hidden="true" />
        </Button>
        <span className="flex size-6 items-center justify-center rounded-md bg-secondary text-secondary [&_svg]:size-3.5">
          <Icon aria-hidden="true" />
        </span>
        <span>{item.kind === "memory" ? "Recall" : (from ?? kind.label)}</span>
        <span aria-hidden="true">·</span>
        <time dateTime={item.updated_at} className="tabular-nums">
          {when(item.updated_at)}
        </time>
        <div className="ml-auto flex items-center gap-0.5">
          <RowAction label={unread ? "Mark read" : "Mark unread"} hint="u" onClick={onRead}>
            <MailOpenIcon aria-hidden="true" />
          </RowAction>
          {item.done_at ? null : (
            <RowAction
              label={snoozed ? "Wake now" : "Snooze until tomorrow"}
              hint="s"
              onClick={onSnooze}
            >
              <AlarmClockIcon aria-hidden="true" />
            </RowAction>
          )}
          <RowAction label={item.done_at ? "Not done" : "Done"} hint="e" onClick={onDone}>
            {item.done_at ? <RotateCcwIcon aria-hidden="true" /> : <CheckIcon aria-hidden="true" />}
          </RowAction>
        </div>
      </div>
      <h2 className="text-lg leading-snug font-semibold text-pretty text-foreground">
        {entryTitle(entry)}
      </h2>
      {entry.scope ? (
        <ul aria-label="Recall notices" className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
          {entry.items.map((i) => (
            <li key={i.id} className="flex items-center gap-2 text-muted-foreground">
              <span className="tabular-nums">{memories(i.count)}</span>
              <span aria-hidden="true">·</span>
              <time dateTime={i.updated_at} className="tabular-nums">
                {when(i.updated_at)}
              </time>
            </li>
          ))}
        </ul>
      ) : item.kind !== "memory" && entryTitle(entry) !== item.summary ? (
        <p className="text-sm text-muted-foreground">{item.summary}</p>
      ) : null}
      {inline ? <div data-slot="inbox-detail-actions">{inline}</div> : null}
      <div className="flex flex-wrap items-center gap-2">
        {reply ? (
          <Button size="sm" onClick={onOpen}>
            <ReplyIcon data-icon="inline-start" />
            Reply
          </Button>
        ) : null}
        <Button
          size="sm"
          variant={reply || item.kind !== "memory" ? "outline" : "default"}
          onClick={onOpen}
        >
          {item.kind === "memory" ? "Review" : openLabel(item)}
        </Button>
      </div>
    </article>
  );
}

/**
 * An approval row beyond the first page of the caller's queue: read by id; inline actions while
 * it is pending (the inbox item went to the approver seat's holders; the server refuses anyone
 * else, shown under the buttons).
 */
function InlineApprovalById({
  api,
  co,
  id,
  onDecided,
  onSettled,
}: {
  api: Api;
  co: string;
  id: string;
  onDecided: (decided: ApprovalRow) => void;
  onSettled: () => void;
}) {
  const q = useQuery(approvalQuery(api, co, id));
  const a = q.data;
  if (!a || a.state === "expired") return null;
  if (a.state === "pending")
    return <InlineApproval api={api} co={co} approval={a} onDecided={onDecided} />;
  return <DecisionOutcome api={api} co={co} approval={a} onSettled={onSettled} />;
}

/** A pending approval in its inbox row: the held post's text, then Approve / Edit / Reject. */
/** MD-STATUS-FIX: a lesson digest's sentences, or null for any other approval. */
export function digestLessons(a: Pick<ApprovalRow, "kind" | "proposal">): string[] | null {
  const c = a.proposal.change as { kind?: unknown; items?: unknown } | null;
  if (a.kind !== "memory_lesson" || c?.kind !== "lesson_digest" || !Array.isArray(c.items))
    return null;
  return c.items.map((i) => String((i as { sentence?: unknown }).sentence ?? ""));
}

export function InlineApproval({
  api,
  co,
  approval,
  onDecided,
}: {
  api: Api;
  co: string;
  approval: ApprovalRow;
  onDecided: (decided: ApprovalRow) => void;
}) {
  const post = heldPostText(approval);
  const slug = useRouterState({ select: (s) => s.location.pathname.split("/")[2] ?? "" });
  const lessons = digestLessons(approval);
  // MD-STATUS-FIX: a digest of several lessons is answered lesson by lesson in the Memory lane,
  // never approved whole from a two-line summary.
  if (lessons && lessons.length > 1)
    return (
      <div data-slot="inbox-approval" className="mt-1.5 flex flex-col gap-1.5">
        <ol className="m-0 list-decimal pl-5 text-sm text-muted-foreground">
          {lessons.map((s, n) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: the digest's order is fixed
            <li key={n}>{s}</li>
          ))}
        </ol>
        <Link
          to="/c/$slug/inbox"
          params={{ slug }}
          search={{ tab: "memory", card: `lesson:${approval.id}` } as never}
          className="self-start text-xs text-primary-text hover:underline"
          data-slot="inbox-review-lessons"
        >
          Review {lessons.length} lessons
        </Link>
      </div>
    );
  const decided = (d: ApprovalRow) => {
    const t = decisionToast(d);
    toast.add({ ...t, description: t.description ?? approval.proposal.title });
    onDecided(d);
  };
  // CORE-CARDS: an escalation card decides by its options
  if (hasCard(approval))
    return (
      <div data-slot="inbox-approval" className="mt-1.5 flex flex-col gap-1.5">
        <EscalationCard
          api={api}
          co={co}
          approval={approval}
          size="xs"
          compact
          onDecided={decided}
        />
      </div>
    );
  return (
    <div data-slot="inbox-approval" className="mt-1.5 flex flex-col gap-1.5">
      {post ? (
        <blockquote className="m-0 line-clamp-3 border-l-2 border-border pl-2 text-sm text-foreground">
          {post}
        </blockquote>
      ) : approval.proposal.summary ? (
        <p className="line-clamp-2 text-sm text-muted-foreground">{approval.proposal.summary}</p>
      ) : null}
      <ApprovalActions api={api} co={co} approval={approval} size="xs" onDecided={decided} />
    </div>
  );
}

/** INBOX-PREFS: an item waiting on the person (the Waiting on me tab). */
const isWaiting = (i: Pick<Item, "kind">) => i.kind === "approval" || i.kind === "agent_escalation";

/** Display names by membership: DEs (the roster's cache) and people (the directory). */
function useMemberNames(api: Api, co: string, enabled: boolean): Map<string, string> {
  const des = useInfiniteQuery({ ...digitalEmployeesQuery(api, co), enabled, retry: false });
  const people = useInfiniteQuery({ ...peopleQuery(api, co), enabled, retry: false });
  return useMemo(() => {
    const out = new Map<string, string>();
    for (const d of (des.data?.pages ?? []).flatMap((pg) => pg.data))
      out.set(d.membership_id, workerName(d.display_name));
    for (const p of (people.data?.pages ?? []).flatMap((pg) => pg.data))
      if (!out.has(p.id)) out.set(p.id, p.display_name);
    return out;
  }, [des.data, people.data]);
}

/** INBOX-READABLE: one inbox row — a single item, or every Recall notice of one memory scope. */
export type Entry = {
  /** The row's key: its item's id, or `scope:<kind>:<id>` for a memory scope's row. */
  key: string;
  /** Newest first. */
  items: Item[];
  lead: Item;
  /** A memory scope's row (Recall's proposal notices folded per scope). */
  scope: MemoryScopeRef | null;
};

/**
 * INBOX-READABLE: Recall's proposal notices fold into one row per memory scope, at the place of
 * the newest (its time); every other item is its own row. Order is kept.
 */
export function groupEntries(items: readonly Item[]): Entry[] {
  const out: Entry[] = [];
  const byScope = new Map<string, Entry>();
  for (const i of items) {
    const scope = i.kind === "memory" && i.subject_kind === "recall_notice" ? i.memory_scope : null;
    if (!scope) {
      out.push({ key: i.id, items: [i], lead: i, scope: null });
      continue;
    }
    const k = `${scope.kind}:${scope.id}`;
    const group = byScope.get(k);
    if (group) {
      group.items.push(i);
      continue;
    }
    // Keyed by its scope, so the row (and the page's open pane) holds as its notices change.
    const entry: Entry = { key: `scope:${k}`, items: [i], lead: i, scope };
    byScope.set(k, entry);
    out.push(entry);
  }
  return out;
}

const SCOPE_KIND: Record<MemoryScopeRef["kind"], string> = {
  company: "company",
  agency: "agency",
  space: "space",
  team: "team",
  seat: "seat",
  agent: "helper",
};

/** An older notice's summary ("Recall: 14 memory proposal(s) waiting in seat; …"). */
const NOTICE = /^Recall: (\d+) memory proposal\(s\) waiting in (\w+)/;

const memories = (n: number) => `${n} ${n === 1 ? "memory" : "memories"} to review`;

/** A seat title's own "(DE)" mark. */
const DE_SUFFIX = /\s*\(DE\)\s*$/i;

/** A row's title: plain words for Recall's notices, the summary otherwise. */
export function entryTitle(e: Entry): string {
  if (e.scope) {
    const n = e.items.reduce((sum, i) => sum + i.count, 0);
    // One kind label: a DE's seat "(DE)", a person's "(seat)", a team "(team)" — a title that
    // already ends in "(DE)" (some seat titles do) is not labelled twice.
    const de =
      e.scope.kind === "seat" && (e.scope.de === true || DE_SUFFIX.test(e.scope.name ?? ""));
    const kind = de ? "DE" : SCOPE_KIND[e.scope.kind];
    const name = e.scope.name?.replace(DE_SUFFIX, "").trim();
    return `${memories(n)} · ${name ? `${name} (${kind})` : `a removed ${kind}`}`;
  }
  const m = e.lead.kind === "memory" ? NOTICE.exec(e.lead.summary) : null;
  return m ? `${memories(Number(m[1]))} · ${m[2]}` : e.lead.summary;
}

/** A waiting row's one-click action: Reply (an agent asks) or Open. */
function WaitingAction({ item, onOpen }: { item: Item; onOpen: () => void }) {
  const action =
    item.kind === "agent_escalation" && item.subject_kind !== "commitment" ? "Reply" : "Open";
  return (
    <Button
      size="xs"
      variant="outline"
      className="relative z-10"
      aria-label={`${action}: ${item.summary}`}
      onClick={onOpen}
    >
      {action === "Reply" ? <ReplyIcon data-icon="inline-start" /> : null}
      {action}
    </Button>
  );
}

/** A row's action icon: subtle until the row is hovered or focused, with its tooltip and key. */
function RowAction({
  label,
  hint,
  onClick,
  children,
}: {
  label: string;
  hint: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            onClick={onClick}
            className="text-faint group-hover/row:text-foreground group-focus-within/row:text-foreground pointer-coarse:text-muted-foreground"
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipContent>
        {label} · <kbd className="font-sans">{hint}</kbd>
      </TooltipContent>
    </Tooltip>
  );
}

function InboxRow({
  entry,
  inline,
  from,
  onOpen,
  onRead,
  onDone,
  onSnooze,
  onReview,
  selected = false,
}: {
  entry: Entry;
  /** AGT-UI-2: an inline decision (a pending approval) or action (Resume a run in review). */
  inline?: ReactNode;
  /** INBOX-PREFS: who a waiting item is from (its display name, when known). */
  from?: string | undefined;
  onOpen: () => void;
  onRead: () => void;
  onDone: () => void;
  onSnooze: () => void;
  /** A memory row's Review: the memory review, filtered to its scope. */
  onReview: () => void;
  /** The split page's open row. */
  selected?: boolean;
}) {
  const item = entry.lead;
  const snoozed = snoozedNow(item);
  const kind = kindOf(item);
  const Icon = kind.icon;
  const unread = entry.items.some((i) => !i.read_at);
  const needsYou = item.kind === "agent_escalation" || item.kind === "approval";
  const waiting = isWaiting(item) && !item.done_at;
  const memory = item.kind === "memory";
  const title = entryTitle(entry);
  // The meta line: source · count · time. A waiting item names who waits and since when.
  const source = memory ? "Recall" : (from ?? (needsYou ? null : kind.label));
  const count =
    entry.items.length > 1
      ? `${entry.items.length} notices`
      : !memory && item.count > 1
        ? `${item.count}×`
        : null;
  return (
    // INBOX-READABLE (founder review 2026-10-30): two lines — the title (up to two lines, wrapped
    // at words) over source · count · time — and the actions in their own right-hand column,
    // always reserved, so they never cover the text.
    <li
      data-list-row=""
      data-item-id={entry.key}
      data-unread={unread || undefined}
      data-scope={entry.scope ? `${entry.scope.kind}:${entry.scope.id}` : undefined}
      aria-current={selected || undefined}
      className="group/row relative flex items-start gap-2.5 border-b border-subtle py-2.5 pr-2 pl-4 hover:bg-sunken aria-[current=true]:bg-sunken has-[[data-row-focus]:focus-visible]:outline-2 has-[[data-row-focus]:focus-visible]:-outline-offset-2 has-[[data-row-focus]:focus-visible]:outline-ring has-[[data-row-focus]:focus-visible]:outline-solid"
    >
      <span className="flex h-6 w-1.5 shrink-0 items-center justify-center">
        {unread ? (
          <span role="img" aria-label="Unread" className="size-1.5 rounded-full bg-primary" />
        ) : null}
      </span>
      <span
        role="img"
        aria-label={kind.label}
        title={kind.label}
        className="flex size-6 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary [&_svg]:size-3.5"
      >
        <Icon aria-hidden="true" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <button
          type="button"
          data-row-focus=""
          title={title}
          onClick={onOpen}
          className={cn(
            "line-clamp-2 min-w-0 text-left text-sm leading-snug text-pretty break-words text-foreground outline-none after:absolute after:inset-0 after:content-['']",
            unread && "font-medium",
          )}
        >
          {title}
        </button>
        <div
          data-slot={waiting ? "inbox-waiting" : "inbox-meta"}
          className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground"
        >
          {source ? (
            <span className={cn("min-w-0", waiting && from && "font-medium text-foreground")}>
              {source}
            </span>
          ) : null}
          {needsYou ? (
            <Badge variant="warning" className="relative z-10">
              {kind.label}
            </Badge>
          ) : null}
          {count ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="tabular-nums">{count}</span>
            </>
          ) : null}
          <span aria-hidden="true">·</span>
          {waiting ? (
            <span className="tabular-nums">waiting {when(item.created_at)}</span>
          ) : (
            <time dateTime={item.updated_at} className="tabular-nums">
              {when(item.updated_at)}
            </time>
          )}
          {waiting && !inline ? <WaitingAction item={item} onOpen={onOpen} /> : null}
          {memory && !item.done_at ? (
            // A scope's notices are decided on the memory review, filtered to that scope.
            <Button
              size="xs"
              variant="outline"
              className="relative z-10 ml-1"
              aria-label={`Review: ${title}`}
              onClick={onReview}
            >
              Review
            </Button>
          ) : null}
        </div>
        {inline ? <div className="relative z-10 pb-1">{inline}</div> : null}
      </div>
      <div
        data-slot="inbox-row-actions"
        className="relative z-10 flex w-[5.75rem] shrink-0 items-center justify-end gap-0.5"
      >
        <RowAction label={unread ? "Mark read" : "Mark unread"} hint="u" onClick={onRead}>
          <MailOpenIcon aria-hidden="true" />
        </RowAction>
        {item.done_at ? null : (
          <RowAction
            label={snoozed ? "Wake now" : "Snooze until tomorrow"}
            hint="s"
            onClick={onSnooze}
          >
            <AlarmClockIcon aria-hidden="true" />
          </RowAction>
        )}
        <RowAction label={item.done_at ? "Not done" : "Done"} hint="e" onClick={onDone}>
          {item.done_at ? <RotateCcwIcon aria-hidden="true" /> : <CheckIcon aria-hidden="true" />}
        </RowAction>
      </div>
    </li>
  );
}

/** Whether the item is snoozed into the future. */
function snoozedNow(item: Item): boolean {
  return item.snoozed_until !== null && new Date(item.snoozed_until).getTime() > Date.now();
}

/** The unread count (the Unread filter's first page). `fetch` false reads the cache only. */
export function useInboxUnread(api: Api, co: string, fetch: boolean): number | undefined {
  const q = useQuery({ ...inboxQuery(api, co, "unread"), enabled: fetch });
  return q.data?.data.length;
}
